/**
 * コンテンツ単位の「放送言語」チェックボックス群 (Issue #449 / Issue #545)。
 *
 * ## なぜ部品として切り出したか
 *
 * 元は `BroadcastContentsEditor` にインラインで書かれていたが、Issue #545 で
 * 「同時に再生できる言語は 8 言語まで」という上限 UI (到達時に未選択を
 * disabled にし、上限である旨を表示する) を追加するにあたり、
 * 分岐が増えてエディタ本体の見通しが悪くなるため独立させた。
 * 上限の判定ロジックを 1 か所に閉じることで、テストも部品単位で書ける。
 *
 * ## 上限 (MAX_SPOT_LANGUAGES) について
 *
 * 言語を増やすほど Polly 合成回数と放送尺が線形に増えるため、
 * バックエンド (`lambda/control-panel-api/index.py` の `MAX_SPOT_LANGUAGES`) と
 * OpenAPI (`languages.maxItems`) が 8 言語で 400 を返す。
 * **画面側で 9 件目を選べてしまうと「保存ボタンを押して初めて 400 で弾かれる」**
 * という分かりにくい失敗になるため、チェックボックス側で到達時に
 * 未選択の言語を disabled にして、上限である旨を明示する。
 */
import type { BroadcastContentCategory } from '../../../api/types';

import {
  BASE_LANGUAGE_KEYS,
  MAX_SPOT_LANGUAGES,
  POLLY_LANGUAGE_OPTIONS,
} from '../../../data/pollyVoices';

/**
 * カテゴリごとに選択できる言語の一覧を返す。
 *
 * - 任意テキスト (`free_text`): Polly マスタの全言語
 *   (受け入れ条件「任意放送は 4 言語に加えその他の言語も選択できる」)。
 * - 遅延放送 (自列車 / 接続線): **基本 4 言語のみ**。定型文マスタが
 *   日英中韓で閉じているため、それ以外は放送文面を持てない。
 */
export function languageOptionsForCategory(
  category: BroadcastContentCategory,
): typeof POLLY_LANGUAGE_OPTIONS {
  if (category === 'free_text') return POLLY_LANGUAGE_OPTIONS;
  return POLLY_LANGUAGE_OPTIONS.filter((language) => BASE_LANGUAGE_KEYS.includes(language.key));
}

export function BroadcastLanguagesField({
  contentIndex,
  category,
  languages,
  onChange,
}: {
  /** `contents[]` の 0 始まり index (aria-label / data-testid を一意にするため) */
  contentIndex: number;
  /** コンテンツのカテゴリ (選択できる言語の絞り込みに使う) */
  category: BroadcastContentCategory;
  /**
   * 現在選択されている language_key の配列。
   * `undefined` は「未設定 = 日本語のみ」(既存データの後方互換)。
   */
  languages: readonly string[] | undefined;
  /** 選択変更時に **マスタ定義順** へ正規化した配列を渡す (放送順序を揃えるため) */
  onChange: (languages: string[]) => void;
}): React.JSX.Element {
  const options = languageOptionsForCategory(category);
  const selected = new Set(languages ?? ['ja']);
  /*
   * 上限判定は「保存される languages の件数」で行う。
   *
   * 表示中の選択肢 (options) だけを数えないのが重要: 任意テキストから遅延放送へ
   * カテゴリを変えた等の経緯で、画面に出ていない言語が保存値に残っている場合も
   * バックエンドは合計件数で 400 を返すため、画面と API の判定を一致させる。
   */
  const limitReached = selected.size >= MAX_SPOT_LANGUAGES;
  /*
   * Issue #555: 放送言語を 1 件も選択しないまま保存できてしまう問題。
   *
   * 選択が 0 件 (`languages: []`) だと放送側は「このコンテンツはどの言語でも
   * 放送しない」と解釈するため、保存できても **そのコンテンツは永久に放送
   * されない**。設定したのに何も起きないのはエラーにならず静かに壊れる典型なので、
   * 保存前バリデーション (`validateContentIssues`) とバックエンドの両方で弾く。
   *
   * ## なぜチェックボックスを disabled にしないのか
   *
   * 上限 (`limitReached`) は「未選択の言語を disabled」で防いでいるが、下限は
   * 同じ手が使えない。「日本語を外して英語だけにする」「言語を総入れ替えする」
   * といった正当な編集の **途中で一時的に 0 件になる** ことがあり、最後の 1 件を
   * 外せなくすると入れ替え操作そのものが詰まってしまう。
   *
   * そこで下限は **操作を止めずに警告を出す** 方針にし、0 件のまま保存しようと
   * した時点でバリデーションが弾く (エラーサマリから該当コンテンツへ飛べる)。
   */
  const noLanguageSelected = selected.size === 0;

  const toggle = (languageKey: string, checked: boolean): void => {
    const next = new Set(selected);
    if (checked) {
      // 上限に達している場合は追加しない (disabled を無視した操作への防御)。
      if (limitReached && !next.has(languageKey)) return;
      next.add(languageKey);
    } else {
      next.delete(languageKey);
    }
    // 放送は「言語ごとに全コンテンツをまとめて流す」順序なので、チェック順ではなく
    // **マスタ定義順** で保存する (サーバ側 `normalize_language_keys` と同じ規則)。
    onChange(POLLY_LANGUAGE_OPTIONS.filter((option) => next.has(option.key)).map((o) => o.key));
  };

  /*
   * 任意テキスト (全 22 言語) はチェックボックスを並べると縦横に場所を取るうえ、
   * アイステイションズ路線の選択 (`IStationsLinesSelect`) と操作が揃わないため、
   * 「選択済み一覧 + 追加用プルダウン」にする。
   * 4 言語しかない遅延放送はチェックボックスのままの方が一覧性が高いので変えない。
   */
  const useDropdown = category === 'free_text';

  // 画面に選択肢として出ている言語のうち、まだ選ばれていないもの (追加用プルダウンの候補)。
  const addableOptions = options.filter((option) => !selected.has(option.key));

  // 保存値に含まれる言語を、マスタ定義順で表示する (選択済み一覧の並び = 放送順)。
  // 表示中の選択肢に無い言語 (カテゴリ変更の経緯で残ったもの) も、件数に数えているため
  // 一覧に出して外せるようにする (出さないと 400 の原因が画面から見えなくなる)。
  const selectedOptions = POLLY_LANGUAGE_OPTIONS.filter((option) => selected.has(option.key));

  if (useDropdown) {
    return (
      <fieldset data-testid={`content-languages-dropdown-${contentIndex}`}>
        <legend>放送言語</legend>
        {noLanguageSelected ? null : (
          <ul className="connecting-route-list">
            {selectedOptions.map((language) => (
              <li
                key={language.key}
                data-testid={`content-language-item-${contentIndex}-${language.key}`}
              >
                <span className="connecting-route-list__name">{language.label}</span>
                <button
                  type="button"
                  className="danger connecting-route-list__remove"
                  aria-label={`コンテンツ#${contentIndex + 1} ${language.label} を削除`}
                  data-testid={`content-language-remove-${contentIndex}-${language.key}`}
                  onClick={() => toggle(language.key, false)}
                >
                  × 削除
                </button>
              </li>
            ))}
          </ul>
        )}
        <select
          aria-label={`コンテンツ#${contentIndex + 1} 放送言語を追加`}
          data-testid={`content-language-add-select-${contentIndex}`}
          value=""
          disabled={limitReached || addableOptions.length === 0}
          onChange={(event) => {
            if (event.target.value) toggle(event.target.value, true);
          }}
        >
          <option value="">
            {limitReached
              ? `(上限 ${MAX_SPOT_LANGUAGES} 言語に達しています)`
              : addableOptions.length === 0
                ? '(追加できる言語がありません)'
                : '(追加する言語を選択)'}
          </option>
          {addableOptions.map((language) => (
            <option key={language.key} value={language.key}>
              {language.label}
            </option>
          ))}
        </select>
        {limitReached ? (
          <div className="hint" data-testid={`content-language-limit-hint-${contentIndex}`}>
            同時に再生できる言語は {MAX_SPOT_LANGUAGES}{' '}
            言語までです。他の言語を選ぶには、いずれかの選択を外してください。
          </div>
        ) : null}
        {noLanguageSelected ? (
          <div
            className="error-message"
            role="alert"
            data-testid={`content-language-required-hint-${contentIndex}`}
          >
            放送言語を 1 つ以上選択してください（未選択のままだと、このコンテンツは放送されません）
          </div>
        ) : null}
      </fieldset>
    );
  }

  return (
    <fieldset>
      <legend>放送言語</legend>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {options.map((language) => {
          const checked = selected.has(language.key);
          return (
            <label key={language.key}>
              <input
                type="checkbox"
                aria-label={`コンテンツ#${contentIndex + 1} ${language.label}`}
                checked={checked}
                // 上限到達後は「今チェックされている言語を外す」操作のみ許可する。
                disabled={limitReached && !checked}
                onChange={(event) => toggle(language.key, event.target.checked)}
              />
              {language.label}
            </label>
          );
        })}
      </div>
      {limitReached ? (
        <div className="hint" data-testid={`content-language-limit-hint-${contentIndex}`}>
          同時に再生できる言語は {MAX_SPOT_LANGUAGES}{' '}
          言語までです。他の言語を選ぶには、いずれかの選択を外してください。
        </div>
      ) : null}
      {/*
        Issue #555: 放送言語が 0 件のままだと保存できない (= そのコンテンツは
        1 言語も放送されない)。保存ボタンを押して初めて気付くのを避けるため、
        選択が空になった時点でその場に警告を出す。
      */}
      {noLanguageSelected ? (
        <div
          className="error-message"
          role="alert"
          data-testid={`content-language-required-hint-${contentIndex}`}
        >
          放送言語を 1 つ以上選択してください（未選択のままだと、このコンテンツは放送されません）
        </div>
      ) : null}
    </fieldset>
  );
}
